import type { QuestionContent } from './question';

export interface StrategyQuestion {
  readonly context: string | null;
  readonly prompt: string;
  readonly content: QuestionContent;
}

export interface StrategyHints {
  readonly nudge: string;
  readonly steps: readonly [string, string, string];
}

type Operator = '+' | '-' | '×' | ':';

interface Sum {
  readonly kind: 'sum';
  readonly left: number;
  readonly operator: Operator;
  readonly right: number;
}

interface PartOf {
  readonly kind: 'fraction' | 'percent';
  readonly numerator: number;
  readonly denominator: number;
  readonly whole: number;
}

type Task = Sum | PartOf;

const NUMBER = String.raw`\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+(?:,\d+)?`;
const OPERATORS: Readonly<Record<string, Operator>> = {
  '+': '+',
  '-': '-',
  '−': '-',
  '–': '-',
  '×': '×',
  x: '×',
  X: '×',
  '*': '×',
  '·': '×',
  ':': ':',
  '÷': ':',
};
const SUM = new RegExp(String.raw`(${NUMBER})(\s*[+\-−–×*·÷]\s*|\s+[:xX]\s+)(${NUMBER})`, 'gu');
const FRACTION_OF = new RegExp(String.raw`(\d+)\s*/\s*(\d+)\s*(?:deel\s+)?van\s+(${NUMBER})`, 'u');
const PERCENT_OF = new RegExp(String.raw`(\d+)\s*%\s*van\s+(${NUMBER})`, 'u');
const LEFTOVER = /\d\s*[+\-−–×*:÷/%]|[+\-−–×*:÷/%]\s*\d/u;

function parseNumber(text: string): number {
  return Number(text.replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.'));
}

function show(value: number): string {
  if (Number.isInteger(value)) {
    return Math.abs(value) >= 10_000 ? value.toLocaleString('nl-NL') : String(value);
  }
  return String(Math.round(value * 1000) / 1000).replace('.', ',');
}

function decimals(value: number): number {
  const [, fraction = ''] = String(value).split('.');
  return fraction.length;
}

function readTask(question: StrategyQuestion): Task | null {
  if (question.context && question.context.trim() !== '') {
    return null;
  }
  const gap =
    question.content.type === 'text' && question.content.gap ? question.content.gap.before : '';
  const text = `${question.prompt} ${gap}`.replace(/\s+/g, ' ');

  const fraction = FRACTION_OF.exec(text);
  if (fraction) {
    const [, numerator = '', denominator = '', whole = ''] = fraction;
    return {
      kind: 'fraction',
      numerator: Number(numerator),
      denominator: Number(denominator),
      whole: parseNumber(whole),
    };
  }
  const percent = PERCENT_OF.exec(text);
  if (percent) {
    const [, share = '', whole = ''] = percent;
    return {
      kind: 'percent',
      numerator: Number(share),
      denominator: 100,
      whole: parseNumber(whole),
    };
  }

  const sums = [...text.matchAll(SUM)];
  const [sum] = sums;
  const same = (match: RegExpExecArray) =>
    match[0].replace(/\s+/g, '') === sum?.[0].replace(/\s+/g, '');
  if (!sum || !sums.every(same)) {
    return null;
  }
  let rest = text;
  for (const match of [...sums].reverse()) {
    rest = rest.slice(0, match.index) + rest.slice(match.index + match[0].length);
  }
  if (LEFTOVER.test(rest)) {
    return null;
  }
  const [, left = '', symbol = '', right = ''] = sum;
  const operator = OPERATORS[symbol.trim()];
  if (!operator) {
    return null;
  }
  return { kind: 'sum', left: parseNumber(left), operator, right: parseNumber(right) };
}

function places(value: number): number[] {
  const parts: number[] = [];
  let unit = 1;
  let rest = value;
  while (rest > 0) {
    const digit = rest % 10;
    if (digit > 0) {
      parts.unshift(digit * unit);
    }
    rest = Math.floor(rest / 10);
    unit *= 10;
  }
  return parts;
}

function joinParts(parts: readonly number[]): string {
  const shown = parts.map(show);
  return shown.length <= 1
    ? (shown[0] ?? '')
    : `${shown.slice(0, -1).join(', ')} en ${shown.at(-1) ?? ''}`;
}

function columnLadder(sum: Sum): StrategyHints {
  const adding = sum.operator === '+';
  const comma = decimals(sum.left) > 0 || decimals(sum.right) > 0;
  return {
    nudge: comma
      ? 'Tip: zet de komma’s precies onder elkaar.'
      : 'Tip: zet de getallen netjes onder elkaar.',
    steps: [
      comma
        ? `Zet ${show(sum.left)} en ${show(sum.right)} onder elkaar, met de komma’s precies onder elkaar. Vul lege plekken aan met een 0.`
        : `Zet ${show(sum.left)} en ${show(sum.right)} onder elkaar: eenheden onder eenheden, tientallen onder tientallen.`,
      adding
        ? 'Begin helemaal rechts. Tel die kolom op en schrijf alleen het laatste cijfer op.'
        : 'Begin helemaal rechts. Haal het onderste cijfer van het bovenste af.',
      adding
        ? 'Kom je in een kolom boven de 9? Onthoud dan 1 en tel die bij de volgende kolom op.'
        : 'Is het bovenste cijfer te klein? Leen dan 1 van de kolom links ernaast; dan komt er 10 bij.',
    ],
  };
}

function addLadder(sum: Sum): StrategyHints {
  const start = Math.max(sum.left, sum.right);
  const extra = Math.min(sum.left, sum.right);
  const begin =
    start !== sum.left
      ? `Begin bij het grootste getal, ${show(start)}.`
      : `Begin bij ${show(start)}.`;
  if (extra < 10) {
    const fill = (10 - (start % 10)) % 10;
    if (fill > 0 && fill < extra) {
      return {
        nudge: 'Tip: maak eerst het tiental vol.',
        steps: [
          `${begin} Je moet er ${show(extra)} bij doen.`,
          `Maak eerst het tiental vol: ${show(start)} + ${show(fill)} = ${show(start + fill)}.`,
          `Van de ${show(extra)} heb je er al ${show(fill)} gebruikt. Hoeveel is ${show(start + fill)} + ${show(extra - fill)}?`,
        ],
      };
    }
    return {
      nudge: 'Tip: begin bij het grootste getal en tel verder.',
      steps: [
        `${begin} Tel er ${show(extra)} bij.`,
        `Tel in stapjes van 1 verder vanaf ${show(start)}. Gebruik je vingers als dat helpt.`,
        start < 10
          ? `Steek eerst ${show(start)} vingers op en daarna nog ${show(extra)}. Hoeveel zijn het er samen?`
          : `Kijk alleen naar de eenheden: ${show(start % 10)} + ${show(extra)}. De tientallen blijven hetzelfde.`,
      ],
    };
  }
  const parts = places(extra);
  const [first = 0, ...others] = parts;
  const rest = others.reduce((total, part) => total + part, 0);
  if (rest === 0) {
    return {
      nudge: 'Tip: spring in sprongen van 10.',
      steps: [
        `${begin} Je doet er ${show(extra / 10)} ${extra === 10 ? 'tiental' : 'tientallen'} bij.`,
        `Alleen de tientallen veranderen. De eenheden blijven ${show(start % 10)}.`,
        `Spring vanaf ${show(start)} steeds 10 verder. Tel je sprongen tot je er ${show(extra / 10)} hebt gemaakt.`,
      ],
    };
  }
  return {
    nudge: 'Tip: splits het tweede getal in handige stukjes.',
    steps: [
      `${begin} Splits ${show(extra)} in ${joinParts(parts)}.`,
      `Tel eerst ${show(first)} bij ${show(start)} op. Daarna doe je er nog ${show(rest)} bij.`,
      `${show(start)} + ${show(first)} = ${show(start + first)}. Hoeveel is ${show(start + first)} + ${show(rest)}?`,
    ],
  };
}

function subtractLadder(sum: Sum): StrategyHints {
  const { left, right } = sum;
  const difference = left - right;
  if (right >= 10 && difference > 0 && difference <= 10) {
    return {
      nudge: 'Tip: de getallen liggen dicht bij elkaar. Tel op vanaf het kleinste.',
      steps: [
        `${show(left)} en ${show(right)} liggen dicht bij elkaar. Tel op vanaf ${show(right)} tot je bij ${show(left)} bent.`,
        `Hoeveel stapjes zet je van ${show(right)} naar ${show(left)}? Tel ze op je vingers.`,
        `Controleer je antwoord: ${show(right)} + jouw antwoord moet ${show(left)} zijn.`,
      ],
    };
  }
  if (right < 10) {
    const units = left % 10;
    if (units > 0 && units < right) {
      return {
        nudge: 'Tip: ga eerst terug naar het tiental.',
        steps: [
          `Begin bij ${show(left)} en haal er ${show(right)} af.`,
          `Spring eerst terug naar het tiental: ${show(left)} − ${show(units)} = ${show(left - units)}.`,
          `Van de ${show(right)} heb je er al ${show(units)} afgehaald. Hoeveel is ${show(left - units)} − ${show(right - units)}?`,
        ],
      };
    }
    return {
      nudge: 'Tip: tel terug vanaf het grootste getal.',
      steps: [
        `Begin bij ${show(left)} en tel ${show(right)} stapjes terug.`,
        left < 10
          ? `Steek ${show(left)} vingers op en vouw er ${show(right)} weg.`
          : `Kijk naar de eenheden: ${show(units)} − ${show(right)}. De tientallen blijven hetzelfde.`,
        `Controleer je antwoord: jouw antwoord + ${show(right)} moet ${show(left)} zijn.`,
      ],
    };
  }
  const parts = places(right);
  const [first = 0, ...others] = parts;
  const rest = others.reduce((total, part) => total + part, 0);
  if (rest === 0) {
    return {
      nudge: 'Tip: spring terug in sprongen van 10.',
      steps: [
        `Begin bij ${show(left)}. Je haalt er ${show(right / 10)} ${right === 10 ? 'tiental' : 'tientallen'} af.`,
        `Alleen de tientallen veranderen. De eenheden blijven ${show(left % 10)}.`,
        `Spring vanaf ${show(left)} steeds 10 terug. Tel je sprongen tot je er ${show(right / 10)} hebt gemaakt.`,
      ],
    };
  }
  return {
    nudge: 'Tip: haal het tweede getal er in stukjes af.',
    steps: [
      `Splits ${show(right)} in ${joinParts(parts)}.`,
      `Haal eerst ${show(first)} van ${show(left)} af. Daarna haal je er nog ${show(rest)} af.`,
      `${show(left)} − ${show(first)} = ${show(left - first)}. Hoeveel is ${show(left - first)} − ${show(rest)}?`,
    ],
  };
}

function anchorFor(factor: number, avoid: readonly number[]): number {
  const options = factor >= 8 ? [10, 5, 2] : factor >= 4 ? [5, 10, 2] : [2, 10, 5];
  return options.find((anchor) => anchor !== factor && !avoid.includes(anchor)) ?? 10;
}

function trailingZeros(value: number): number {
  let zeros = 0;
  let rest = value;
  while (rest >= 10 && rest % 10 === 0) {
    zeros += 1;
    rest /= 10;
  }
  return zeros;
}

function multiplyLadder(sum: Sum): StrategyHints {
  const { left, right } = sum;
  const product = left * right;
  if (left <= 10 && right <= 10) {
    const anchor = anchorFor(left, [product]);
    const anchored = anchor * right;
    const gap = left - anchor;
    const step3 =
      gap > 0
        ? `${show(anchor)} × ${show(right)} = ${show(anchored)}. Doe er nog ${show(gap)} × ${show(right)} bij.`
        : `${show(anchor)} × ${show(right)} = ${show(anchored)}. Haal er ${show(-gap)} × ${show(right)} af.`;
    return {
      nudge: `Dit is de tafel van ${show(right)}. Ken je hem al?`,
      steps: [
        `Dit is de tafel van ${show(right)}. Welke som uit die tafel weet je al zeker?`,
        `${show(left)} × ${show(right)} betekent ${show(left)} groepjes van ${show(right)}. Begin bij ${show(anchor)} × ${show(right)}.`,
        step3,
      ],
    };
  }
  const zerosLeft = trailingZeros(left);
  const zerosRight = trailingZeros(right);
  if (
    zerosLeft + zerosRight > 0 &&
    left / 10 ** zerosLeft <= 12 &&
    right / 10 ** zerosRight <= 12
  ) {
    const small = `${show(left / 10 ** zerosLeft)} × ${show(right / 10 ** zerosRight)}`;
    const zeros = zerosLeft + zerosRight;
    return {
      nudge: 'Tip: reken eerst zonder de nullen.',
      steps: [
        `Laat de nullen even weg. Wat is ${small}?`,
        `Tel hoeveel nullen je hebt weggelaten: ${String(zeros)}.`,
        `Zet achter de uitkomst van ${small} ${zeros === 1 ? 'die nul' : `die ${String(zeros)} nullen`} weer terug.`,
      ],
    };
  }
  const split = left >= right ? left : right;
  const other = split === left ? right : left;
  const parts = places(split);
  const [first = 0, ...others] = parts;
  const rest = others.reduce((total, part) => total + part, 0);
  return {
    nudge: 'Tip: splits een getal in tientallen en eenheden.',
    steps: [
      `Splits ${show(split)} in ${joinParts(parts)}.`,
      `Reken ${show(first)} × ${show(other)} en ${show(rest)} × ${show(other)} apart uit.`,
      `${show(first)} × ${show(other)} = ${show(first * other)}. Tel daar ${show(rest)} × ${show(other)} bij op.`,
    ],
  };
}

function divideLadder(sum: Sum): StrategyHints | null {
  const { left, right } = sum;
  if (right === 0 || !Number.isInteger(left) || !Number.isInteger(right)) {
    return null;
  }
  const quotient = left / right;
  const flip = `Draai de som om! ${show(right)} × hoeveel = ${show(left)}?`;
  if (!Number.isInteger(quotient)) {
    return {
      nudge: 'Tip: een deelsom kun je omdraaien naar een keersom.',
      steps: [
        `Hoe vaak past ${show(right)} helemaal in ${show(left)}? ${flip}`,
        `Zoek in de tafel van ${show(right)} het getal dat net onder ${show(left)} zit.`,
        `Weet je hoe vaak ${show(right)} er helemaal in past? Kijk dan hoeveel er nog overblijft tot ${show(left)}.`,
      ],
    };
  }
  if (quotient <= 10) {
    const anchor = anchorFor(quotient, [quotient]);
    return {
      nudge: 'Tip: een deelsom kun je omdraaien naar een keersom.',
      steps: [
        flip,
        `Zeg de tafel van ${show(right)} op tot je bij ${show(left)} komt.`,
        `${show(anchor)} × ${show(right)} = ${show(anchor * right)}. Moet je vanaf daar verder of terug om bij ${show(left)} te komen?`,
      ],
    };
  }
  const tenfold = right * 10;
  if (quotient % 10 === 0 && quotient <= 100) {
    return {
      nudge: 'Tip: een deelsom kun je omdraaien naar een keersom.',
      steps: [
        `${flip} Begin eens met ${show(right)} × 10.`,
        `${show(right)} × 10 = ${show(tenfold)}. Hoe vaak past ${show(tenfold)} in ${show(left)}?`,
        `Reken eerst ${show(left / 10)} : ${show(right)} uit. Doe die uitkomst daarna keer 10.`,
      ],
    };
  }
  const tens = Math.floor(quotient / 10) * 10;
  const first = right * tens;
  const rest = left - first;
  return {
    nudge: 'Tip: een deelsom kun je omdraaien naar een keersom.',
    steps: [
      `${flip} Begin eens met ${show(right)} × 10.`,
      `Splits ${show(left)} in ${show(first)} en ${show(rest)}. Die kun je allebei makkelijk door ${show(right)} delen.`,
      `${show(first)} : ${show(right)} = ${show(tens)}. Hoeveel is ${show(rest)} : ${show(right)}? Tel de twee uitkomsten op.`,
    ],
  };
}

function fractionLadder(task: PartOf): StrategyHints | null {
  const { numerator, denominator, whole } = task;
  if (denominator === 0 || !Number.isInteger(whole / denominator)) {
    return null;
  }
  const piece = whole / denominator;
  return {
    nudge: `Tip: verdeel ${show(whole)} eerst in ${show(denominator)} gelijke stukken.`,
    steps: [
      `Verdeel ${show(whole)} in ${show(denominator)} gelijke stukken. Hoe groot is één stuk?`,
      `Eén stuk is ${show(whole)} : ${show(denominator)}. Je hebt ${show(numerator)} van die stukken nodig.`,
      numerator > 1
        ? `${show(whole)} : ${show(denominator)} = ${show(piece)}. Hoeveel is ${show(numerator)} × ${show(piece)}?`
        : `Controleer je antwoord: ${show(denominator)} keer jouw antwoord moet weer ${show(whole)} zijn.`,
    ],
  };
}

function percentLadder(task: PartOf): StrategyHints | null {
  const share = task.numerator;
  const { whole } = task;
  const tenth = `Begin met 10% van ${show(whole)}: dat is ${show(whole)} : 10.`;
  const nudge = 'Tip: bedenk welk deel van het geheel dit percentage is.';
  if (share === 50 || share === 25 || share === 75) {
    return {
      nudge,
      steps: [
        `Welk deel is ${show(share)}%? Denk aan de helft, een kwart of drie kwart.`,
        share === 50
          ? '50% is de helft.'
          : share === 25
            ? '25% is een kwart: de helft van de helft.'
            : '75% is drie kwart: 50% en 25% samen.',
        share === 50
          ? `Verdeel ${show(whole)} in 2 gelijke stukken.`
          : share === 25
            ? `Neem de helft van ${show(whole)} en daarvan nog eens de helft.`
            : `Reken 50% en 25% van ${show(whole)} uit en tel ze op.`,
      ],
    };
  }
  if (share === 10 || share === 1) {
    const parts = share === 10 ? 10 : 100;
    return {
      nudge,
      steps: [
        `${show(share)}% is één ${share === 10 ? 'tiende' : 'honderdste'} deel.`,
        `Verdeel ${show(whole)} in ${show(parts)} gelijke stukken.`,
        `Controleer je antwoord: ${show(parts)} keer jouw antwoord moet weer ${show(whole)} zijn.`,
      ],
    };
  }
  if (share === 5) {
    return {
      nudge,
      steps: [
        tenth,
        '5% is de helft van 10%.',
        `Reken 10% van ${show(whole)} uit en neem daar de helft van.`,
      ],
    };
  }
  if (share % 10 === 0 && share < 100) {
    return {
      nudge,
      steps: [
        tenth,
        `${show(share)}% is ${show(share / 10)} keer 10%.`,
        `10% van ${show(whole)} = ${show(whole / 10)}. Hoeveel is ${show(share / 10)} × ${show(whole / 10)}?`,
      ],
    };
  }
  return null;
}

function ladderFor(task: Task): StrategyHints | null {
  if (task.kind === 'sum') {
    return sumLadder(task);
  }
  return task.kind === 'fraction' ? fractionLadder(task) : percentLadder(task);
}

function roundLadder(task: Sum): StrategyHints | null {
  const zeros = Math.min(trailingZeros(task.left), trailingZeros(task.right));
  if (zeros < 2 || task.operator === '×' || task.operator === ':') {
    return null;
  }
  const small = `${show(task.left / 10 ** zeros)} ${task.operator === '+' ? '+' : '−'} ${show(task.right / 10 ** zeros)}`;
  const word = zeros === 2 ? 'honderdtallen' : 'duizendtallen';
  return {
    nudge: `Tip: reken met ${word}.`,
    steps: [
      `Laat de nullen even weg. Wat is ${small}?`,
      `Je rekent nu met ${word}: ${small} ${word}.`,
      `Zet achter de uitkomst van ${small} weer ${String(zeros)} nullen.`,
    ],
  };
}

function sumLadder(task: Sum): StrategyHints | null {
  const round =
    Number.isInteger(task.left) && Number.isInteger(task.right) ? roundLadder(task) : null;
  if (round) {
    return round;
  }
  const integers = Number.isInteger(task.left) && Number.isInteger(task.right);
  switch (task.operator) {
    case '+':
      return integers &&
        Math.max(task.left, task.right) < 1000 &&
        Math.min(task.left, task.right) < 100
        ? addLadder(task)
        : columnLadder(task);
    case '-':
      if (task.left < task.right) {
        return null;
      }
      return integers &&
        ((task.left < 1000 && task.right < 100) ||
          (task.right >= 10 && task.left - task.right <= 10))
        ? subtractLadder(task)
        : columnLadder(task);
    case '×':
      return integers && task.left > 0 && task.right > 0 && task.left * task.right < 100_000
        ? multiplyLadder(task)
        : null;
    case ':':
      return divideLadder(task);
  }
}

function answerOf(task: Task): number | null {
  if (task.kind !== 'sum') {
    return (task.whole * task.numerator) / task.denominator;
  }
  switch (task.operator) {
    case '+':
      return task.left + task.right;
    case '-':
      return task.left - task.right;
    case '×':
      return task.left * task.right;
    case ':':
      return task.right === 0 ? null : task.left / task.right;
  }
}

function numbersIn(text: string): number[] {
  return [...text.matchAll(new RegExp(NUMBER, 'gu'))].map(([token]) => parseNumber(token));
}

const BLANK = String.raw`(?:…|\.{2,3}|_+|\?)`;
const MISSING = new RegExp(
  String.raw`(${NUMBER}|${BLANK})\s*([+\-−–×*·÷]|\s[:xX]\s)\s*(${NUMBER}|${BLANK})\s*=\s*(${NUMBER})`,
  'u',
);
const SPLIT = new RegExp(String.raw`(${NUMBER})\s+is\s+(${NUMBER})\s+en\s+${BLANK}`, 'u');
const AVERAGE = new RegExp(
  String.raw`gemiddelde\s+van\s+((?:${NUMBER})(?:(?:,\s+|\s+en\s+)(?:${NUMBER}))+)`,
  'iu',
);
const ROUND = /rond\s+(\d+)\s+af\s+op\s+(tientallen|honderdtallen|duizendtallen)/iu;
const UNIT_FACTORS: Readonly<Record<string, number>> = {
  kilometer: 1_000_000,
  meter: 1000,
  decimeter: 100,
  centimeter: 10,
  millimeter: 1,
  kilogram: 1_000_000,
  gram: 1000,
  milligram: 1,
  liter: 1000,
  deciliter: 100,
  centiliter: 10,
  milliliter: 1,
};
const UNIT_FAMILIES: Readonly<Record<string, string>> = {
  kilometer: 'lengte',
  meter: 'lengte',
  decimeter: 'lengte',
  centimeter: 'lengte',
  millimeter: 'lengte',
  kilogram: 'gewicht',
  gram: 'gewicht',
  milligram: 'gewicht',
  liter: 'inhoud',
  deciliter: 'inhoud',
  centiliter: 'inhoud',
  milliliter: 'inhoud',
};
const UNITS = Object.keys(UNIT_FACTORS).join('|');
const CONVERT = new RegExp(String.raw`(${NUMBER})\s+(${UNITS})\s+is\s+hoeveel\s+(${UNITS})`, 'iu');

interface Special {
  readonly ladder: StrategyHints;
  readonly answer: number;
}

function isBlank(token: string): boolean {
  return new RegExp(`^${BLANK}$`, 'u').test(token);
}

function missingLadder(text: string): Special | null {
  const match = MISSING.exec(text);
  if (!match) {
    return null;
  }
  const [, first = '', symbol = '', second = '', result = ''] = match;
  const operator = OPERATORS[symbol.trim()];
  const blankFirst = isBlank(first);
  if (!operator || blankFirst === isBlank(second)) {
    return null;
  }
  const known = parseNumber(blankFirst ? second : first);
  const total = parseNumber(result);
  switch (operator) {
    case '+':
      return {
        answer: total - known,
        ladder: {
          nudge: 'Tip: tel verder tot je bij de uitkomst bent.',
          steps: [
            `Hoeveel moet er bij ${show(known)} om ${show(total)} te krijgen?`,
            `Tel verder vanaf ${show(known)} tot je bij ${show(total)} bent. Tel je stapjes.`,
            `Je kunt ook ${show(total)} − ${show(known)} uitrekenen.`,
          ],
        },
      };
    case '-':
      return blankFirst
        ? {
            answer: total + known,
            ladder: {
              nudge: 'Tip: denk andersom, met een plussom.',
              steps: [
                `Je zoekt het getal waar je ${show(known)} van afhaalt en dan ${show(total)} overhoudt.`,
                'Denk andersom: dan wordt het een plussom.',
                `Reken ${show(total)} + ${show(known)} uit.`,
              ],
            },
          }
        : {
            answer: known - total,
            ladder: {
              nudge: 'Tip: tel op vanaf de uitkomst.',
              steps: [
                `Hoeveel moet je van ${show(known)} afhalen om ${show(total)} over te houden?`,
                `Tel op vanaf ${show(total)} tot je bij ${show(known)} bent.`,
                `Je kunt ook ${show(known)} − ${show(total)} uitrekenen.`,
              ],
            },
          };
    case '×':
      return {
        answer: total / known,
        ladder: {
          nudge: `Tip: zeg de tafel van ${show(known)} op.`,
          steps: [
            `Welk getal keer ${show(known)} is ${show(total)}?`,
            `Zeg de tafel van ${show(known)} op tot je bij ${show(total)} komt.`,
            `Je kunt ook ${show(total)} : ${show(known)} uitrekenen.`,
          ],
        },
      };
    case ':':
      return blankFirst
        ? {
            answer: total * known,
            ladder: {
              nudge: 'Tip: denk andersom, met een keersom.',
              steps: [
                `Welk getal gedeeld door ${show(known)} is ${show(total)}?`,
                'Denk andersom: dan wordt het een keersom.',
                `Reken ${show(total)} × ${show(known)} uit.`,
              ],
            },
          }
        : {
            answer: known / total,
            ladder: {
              nudge: 'Tip: denk andersom, met een keersom.',
              steps: [
                `${show(known)} gedeeld door hoeveel is ${show(total)}?`,
                `Denk andersom: ${show(total)} × hoeveel = ${show(known)}?`,
                `Je kunt ook ${show(known)} : ${show(total)} uitrekenen.`,
              ],
            },
          };
  }
}

function specialLadder(text: string): Special | null {
  const split = SPLIT.exec(text);
  if (split) {
    const whole = parseNumber(split[1] ?? '');
    const part = parseNumber(split[2] ?? '');
    return {
      answer: whole - part,
      ladder: {
        nudge: 'Tip: welk getal maakt het samen compleet?',
        steps: [
          `Welk getal hoort bij ${show(part)} om samen ${show(whole)} te maken?`,
          `Steek ${show(whole)} vingers op en vouw er ${show(part)} weg. Hoeveel blijven er staan?`,
          `Tel verder vanaf ${show(part)} tot je bij ${show(whole)} bent. Tel je stapjes.`,
        ],
      },
    };
  }
  const average = AVERAGE.exec(text);
  if (average) {
    const values = (average[1] ?? '').split(/,\s+|\s+en\s+/u).map(parseNumber);
    const sum = values.reduce((total, value) => total + value, 0);
    const listed = values.map(show);
    return {
      answer: sum / values.length,
      ladder: {
        nudge: 'Tip: eerst optellen, dan delen.',
        steps: [
          `Tel eerst ${listed.slice(0, -1).join(', ')} en ${listed.at(-1) ?? ''} bij elkaar op.`,
          `Deel die som door ${show(values.length)}, want het zijn ${show(values.length)} getallen.`,
          `Controleer: ligt je antwoord tussen ${show(Math.min(...values))} en ${show(Math.max(...values))}?`,
        ],
      },
    };
  }
  const round = ROUND.exec(text);
  if (round) {
    const value = Number(round[1] ?? '');
    const unitWord = (round[2] ?? '').toLowerCase();
    const unit = unitWord === 'tientallen' ? 10 : unitWord === 'honderdtallen' ? 100 : 1000;
    const lookAt =
      unitWord === 'tientallen'
        ? 'eenheden'
        : unitWord === 'honderdtallen'
          ? 'tientallen'
          : 'honderdtallen';
    return {
      answer: Math.round(value / unit) * unit,
      ladder: {
        nudge: `Tip: kijk naar het cijfer van de ${lookAt}.`,
        steps: [
          `Tussen welke twee ${unitWord} ligt ${show(value)}?`,
          `Kijk naar het cijfer van de ${lookAt} in ${show(value)}.`,
          'Is dat cijfer 5 of meer? Dan rond je naar boven af. Is het minder dan 5? Dan rond je naar beneden af.',
        ],
      },
    };
  }
  const convert = CONVERT.exec(text);
  if (convert) {
    const amount = parseNumber(convert[1] ?? '');
    const from = (convert[2] ?? '').toLowerCase();
    const to = (convert[3] ?? '').toLowerCase();
    const fromFactor = UNIT_FACTORS[from];
    const toFactor = UNIT_FACTORS[to];
    if (!fromFactor || !toFactor || UNIT_FAMILIES[from] !== UNIT_FAMILIES[to] || from === to) {
      return null;
    }
    const bigger = fromFactor > toFactor;
    const ratio = bigger ? fromFactor / toFactor : toFactor / fromFactor;
    return {
      answer: bigger ? amount * ratio : amount / ratio,
      ladder: {
        nudge: 'Tip: denk aan het trapje van de maten.',
        steps: [
          bigger ? `1 ${from} is ${show(ratio)} ${to}.` : `1 ${to} is ${show(ratio)} ${from}.`,
          bigger
            ? `Je hebt ${show(amount)} ${from}. Dat zijn ${show(amount)} keer zoveel ${to}.`
            : `Hoeveel keer past ${show(ratio)} in ${show(amount)}?`,
          bigger
            ? `Reken ${show(amount)} × ${show(ratio)} uit.`
            : `Reken ${show(amount)} : ${show(ratio)} uit.`,
        ],
      },
    };
  }
  return missingLadder(text);
}

function isSafe(ladder: StrategyHints, answer: number): boolean {
  return [ladder.nudge, ...ladder.steps].every(
    (line) => !numbersIn(line).some((value) => Math.abs(value - answer) < 1e-9),
  );
}

export function strategyHints(question: StrategyQuestion): StrategyHints | null {
  if (question.context && question.context.trim() !== '') {
    return null;
  }
  const gap =
    question.content.type === 'text' && question.content.gap ? question.content.gap.before : '';
  const special = specialLadder(`${question.prompt} ${gap}`.replace(/\s+/g, ' '));
  if (special) {
    return Number.isFinite(special.answer) && isSafe(special.ladder, special.answer)
      ? special.ladder
      : null;
  }
  const task = readTask(question);
  if (!task) {
    return null;
  }
  const ladder = ladderFor(task);
  const answer = answerOf(task);
  if (!ladder || answer === null) {
    return null;
  }
  return isSafe(ladder, answer) ? ladder : null;
}
